import { ClockCircleOutlined, HistoryOutlined, RightOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { Alert, Button, Collapse, Drawer, Empty, Flex, Pagination, Spin, Tag } from 'antd';
import dayjs from 'dayjs';
import { type FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useGetWidgetChangeDetailsQuery,
  type WidgetChange,
  type WidgetChangeBucket,
  type WidgetChangesRequest,
  type WidgetChangesTimeline as WidgetChangesTimelineData,
} from '@shared/api/widget-changes-api';

import styles from '../analytics.module.scss';

type Props = {
  request: WidgetChangesRequest | null;
  data?: WidgetChangesTimelineData;
  isLoading: boolean;
  isError: boolean;
  onRefetch: () => void;
  selectedDate?: string | null;
  onSelectDate?: (date: string | null) => void;
  demo?: boolean;
};

const PAGE_SIZE = 20;

export const WidgetChangesTimeline: FC<Props> = ({
  request,
  data,
  isLoading,
  isError,
  onRefetch,
  selectedDate,
  onSelectDate,
  demo = false,
}) => {
  const { t } = useTranslation('translation');
  const [internalDate, setInternalDate] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const activeDate = selectedDate === undefined ? internalDate : selectedDate;
  const selectDate = (date: string | null) => {
    if (onSelectDate) onSelectDate(date);
    else setInternalDate(date);
    setPage(1);
  };

  useEffect(() => {
    setInternalDate(null);
    onSelectDate?.(null);
    setPage(1);
    // A different period or channel selection invalidates the open bucket.
  }, [request, onSelectDate]);

  const detailArgs =
    request && activeDate && !demo
      ? { ...request, bucket_start: activeDate, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE }
      : undefined;
  const details = useGetWidgetChangeDetailsQuery(detailArgs ?? skipToken);
  const demoBucket = data?.buckets.find((bucket) => bucket.date === activeDate);
  const currentDetails = demo
    ? demoBucket && {
        date: demoBucket.date,
        total_count: demoBucket.latest.length,
        group_count: demoBucket.change_count,
        changes: demoBucket.latest,
      }
    : details.currentData;
  const selectedBucket = data?.buckets.find((bucket) => bucket.date === activeDate);

  const groups = useMemo(() => {
    const result = new Map<string, WidgetChange[]>();
    for (const change of currentDetails?.changes ?? []) {
      const group = result.get(change.change_group) ?? [];
      group.push(change);
      result.set(change.change_group, group);
    }
    return Array.from(result.entries());
  }, [currentDetails]);

  const propertyLabel = (change: WidgetChange) => {
    const prefix =
      change.entity_type === 'goal'
        ? 'goal_'
        : change.entity_type === 'widget_settings' &&
            ['name', 'description', 'personal_data_policy_url'].includes(change.property_name)
          ? 'preset_'
          : '';
    const key = `${prefix}${change.property_name.replaceAll('.', '_')}`;
    const fallback = change.property_name.split('.').join(' · ').replaceAll('_', ' ');
    const label = t(`analytics.widget.changes.properties.${key}`, { defaultValue: fallback });
    return change.entity_name &&
      (change.entity_type === 'knowledge' || change.entity_type === 'goal')
      ? `${label} · ${change.entity_name}`
      : label;
  };

  const renderValue = (value: unknown, name?: string | null) => {
    if (value === null || value === undefined) return t('analytics.widget.changes.emptyValue');
    if (name && typeof value === 'number') return `${name} (#${value})`;
    if (name && typeof value === 'object') return `${name}\n${JSON.stringify(value, null, 2)}`;
    if (typeof value === 'boolean')
      return t(value ? 'analytics.widget.changes.yes' : 'analytics.widget.changes.no');
    if (typeof value === 'string') return value || t('analytics.widget.changes.emptyValue');
    return JSON.stringify(value, null, 2);
  };

  const renderChange = (change: WidgetChange) => (
    <article key={change.id} className={styles.changeItem}>
      <Flex align="center" gap={8} wrap>
        <Tag
          color={change.action === 'added' ? 'green' : change.action === 'removed' ? 'red' : 'blue'}
        >
          {t(`analytics.widget.changes.actions.${change.action}`)}
        </Tag>
        <strong>
          {propertyLabel(change)}
          {change.position != null ? ` · ${change.position}` : ''}
        </strong>
        <span className={styles.changeTime}>{dayjs(change.created_at).format('HH:mm:ss')}</span>
      </Flex>
      <div className={styles.changeDiff}>
        {change.action !== 'added' && (
          <div className={styles.changeBefore}>
            <span>{t('analytics.widget.changes.before')}</span>
            <pre>{renderValue(change.old_value, change.old_value_name)}</pre>
          </div>
        )}
        {change.action !== 'removed' && (
          <div className={styles.changeAfter}>
            <span>{t('analytics.widget.changes.after')}</span>
            <pre>{renderValue(change.new_value, change.new_value_name)}</pre>
          </div>
        )}
      </div>
    </article>
  );

  const renderBucket = (bucket: WidgetChangeBucket) => (
    <button
      key={bucket.date}
      type="button"
      className={styles.changeBucket}
      onClick={() => selectDate(bucket.date)}
      aria-label={`${dayjs(bucket.date).format('DD.MM.YYYY HH:mm')}: ${bucket.change_count}`}
    >
      <span className={styles.changeBucketIcon}>
        <HistoryOutlined />
      </span>
      <span className={styles.changeBucketBody}>
        <strong>
          {dayjs(bucket.date).format(data?.interval === 'hour' ? 'DD.MM · HH:00' : 'DD.MM.YYYY')}
        </strong>
        <span>{t('analytics.widget.changes.count', { count: bucket.change_count })}</span>
        <small>{bucket.latest.slice(0, 2).map(propertyLabel).join(' · ')}</small>
      </span>
      <RightOutlined className={styles.changeBucketArrow} />
    </button>
  );

  return (
    <section className={styles.changesSection}>
      <Flex align="center" justify="space-between" gap={12} wrap>
        <div>
          <h3 className={styles.changesTitle}>{t('analytics.widget.changes.title')}</h3>
          <span className={styles.changesHint}>{t('analytics.widget.changes.hint')}</span>
          {demo && <Tag color="purple">{t('analytics.widget.changes.demo')}</Tag>}
        </div>
        {isError && <Button onClick={onRefetch}>{t('analytics.widget.changes.retry')}</Button>}
      </Flex>
      {isLoading && (
        <div className={styles.changesLoading}>
          <Spin />
        </div>
      )}
      {!isLoading && isError && (
        <Alert type="error" showIcon message={t('analytics.widget.changes.loadError')} />
      )}
      {!isLoading && !isError && !data?.buckets.length && (
        <div className={styles.changesEmpty}>
          <Empty description={t('analytics.widget.changes.noChanges')} />
        </div>
      )}
      {!isLoading && !isError && Boolean(data?.buckets.length) && (
        <div className={styles.changeBuckets}>
          {data?.buckets.slice().reverse().map(renderBucket)}
        </div>
      )}

      <Drawer
        title={t('analytics.widget.changes.drawerTitle')}
        open={Boolean(activeDate)}
        onClose={() => selectDate(null)}
        width={640}
        styles={{ body: { padding: 20 } }}
      >
        {activeDate && (
          <Flex vertical gap={20}>
            <div className={styles.changeDrawerHeader}>
              <ClockCircleOutlined />
              <strong>
                {dayjs(activeDate).format(
                  data?.interval === 'hour' ? 'DD.MM.YYYY, HH:00' : 'DD.MM.YYYY',
                )}
              </strong>
              <Tag>
                {t('analytics.widget.changes.count', {
                  count: selectedBucket?.change_count ?? currentDetails?.group_count ?? 0,
                })}
              </Tag>
            </div>
            {Boolean(selectedBucket?.latest.length) && (
              <div className={styles.changeLatest}>
                <strong>{t('analytics.widget.changes.latestTitle')}</strong>
                <div className={styles.changeLatestTags}>
                  {selectedBucket?.latest.map((change) => (
                    <Tag key={change.id}>{propertyLabel(change)}</Tag>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h4 className={styles.changesTitle}>{t('analytics.widget.changes.allTitle')}</h4>
              {!demo && details.isFetching && (
                <div className={styles.changesLoading}>
                  <Spin />
                </div>
              )}
              {!demo && !details.isFetching && details.isError && (
                <Alert type="error" showIcon message={t('analytics.widget.changes.loadError')} />
              )}
              {(demo || (!details.isFetching && !details.isError)) && !groups.length && (
                <Empty description={t('analytics.widget.changes.noChanges')} />
              )}
              {(demo || (!details.isFetching && !details.isError)) && Boolean(groups.length) && (
                <Collapse
                  className={styles.changeGroups}
                  items={groups.map(([groupId, changes]) => ({
                    key: groupId,
                    label: (
                      <Flex align="center" gap={8} wrap>
                        <strong>
                          {changes[0].widget_settings_id !== null
                            ? `${t('analytics.widget.changes.preset')} ${changes[0].subject_name ?? `#${changes[0].widget_settings_id}`}`
                            : `${t('analytics.widget.changes.agent')} ${changes[0].subject_name ?? `#${changes[0].agent_id}`}`}
                        </strong>
                        <span className={styles.changeTime}>
                          {dayjs(changes[0].created_at).format('HH:mm:ss')}
                        </span>
                        <Tag>
                          {t('analytics.widget.changes.actor')}{' '}
                          {changes[0].actor_name ?? `#${changes[0].actor_id}`}
                        </Tag>
                        <Tag>
                          {t('analytics.widget.changes.fieldsCount', { count: changes.length })}
                        </Tag>
                      </Flex>
                    ),
                    children: (
                      <Flex vertical gap={12}>
                        {changes.map(renderChange)}
                      </Flex>
                    ),
                  }))}
                />
              )}
              {!demo && (currentDetails?.total_count ?? 0) > PAGE_SIZE && (
                <Pagination
                  className={styles.changesPagination}
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={currentDetails?.total_count}
                  showSizeChanger={false}
                  onChange={setPage}
                />
              )}
            </div>
          </Flex>
        )}
      </Drawer>
    </section>
  );
};
